import { CheckCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ModalShell from '@/components/modal-shell';
import { Button } from '@/components/ui/button';

interface PublishedSuccessModalProps {
  experienceName: string;
  shareLink: string;
  onClose: () => void;
}

export default function PublishedSuccessModal({ experienceName, shareLink, onClose }: PublishedSuccessModalProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(shareLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleShare() {
    if (navigator.share) {
      navigator.share({ title: experienceName, url: shareLink }).catch(() => {});
    } else {
      handleCopy();
    }
  }

  return (
    <ModalShell title={t('modals.publishedSuccess.title')} onClose={onClose}
      icon={<CheckCircle aria-hidden='true' className='h-5 w-5 shrink-0 text-ws-success-fg' />}>
      <div className='flex flex-col gap-4'>
        <div className='text-sm text-ws-text'>
          <p>{t('modals.publishedSuccess.message', { name: experienceName })}</p>
          <p>{t('modals.publishedSuccess.sharePrompt')}</p>
        </div>

        {/* The shell focuses the first real control on open, which is this — and focusing it
            selects the link, so the dialog opens with the thing it exists to hand over already
            selected and one keystroke from being copied. */}
        <input
          readOnly
          aria-label={t('modals.publishedSuccess.linkLabel')}
          value={shareLink}
          className='w-full rounded-lg border border-ws-border bg-ws-surface px-3 py-2 text-sm text-ws-muted'
          onFocus={(e) => e.target.select()}
        />

        <div className='flex items-center gap-2'>
          <Button variant='secondary' onClick={handleCopy} className='flex-1'>
            {copied ? t('modals.publishedSuccess.copied') : t('modals.publishedSuccess.copy')}
          </Button>
          <Button variant='primary' onClick={handleShare} className='flex-1'>
            {t('modals.publishedSuccess.share')}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}
